import { useEffect, useRef } from "react";
import { useVisibilityChange } from "@uidotdev/usehooks";
import dayjs from "dayjs";
import { useAuth } from "react-oidc-context";
import * as z from "zod";
import i18n from "@/config/i18n";
import { loadDayjsLocale, toDayjsLocale } from "@/utils/muiLocale";
import {
  fetchProfileLocale,
  updateStoredProfileLocale,
} from "@/utils/oidcLocale";
import { resolveProfileLocale } from "@/utils/resolveProfileLocale";

async function syncDayjsLocale(locale: string) {
  const resolved = toDayjsLocale(locale);
  if (resolved === "en") {
    dayjs.locale("en");
    return;
  }

  try {
    const loaded = await loadDayjsLocale(resolved);

    if (loaded) {
      dayjs.locale(resolved);
      return;
    }
  } catch {
    // chunk failed to load; fall through to English
  }

  dayjs.locale("en");
}

/** BCP 47 language codes that don't match Zod's locale export name. */
const ZOD_LANGUAGE_ALIASES: Record<string, string> = {
  nb: "no",
};

type ZodLocaleFactory = () => { localeError: z.core.$ZodErrorMap };
const zodLocales = z.locales as Record<string, ZodLocaleFactory | undefined>;

/**
 * Zod exports region-specific locales as camelCase properties
 * (e.g. `z.locales.zhCN` for zh-CN, `z.locales.frCA` for fr-CA)
 * and bare language codes for the rest (e.g. `z.locales.ja`).
 */
function syncZodLocale(locale: string) {
  const [lang = "", region] = locale.split("-");
  const base = ZOD_LANGUAGE_ALIASES[lang] ?? lang;
  const camelKey = region ? `${base}${region.toUpperCase()}` : base;
  const factory = zodLocales[camelKey] ?? zodLocales[base];
  z.config(factory ? factory() : z.locales.en());
}

function getSupportedLngs(): readonly string[] {
  const { supportedLngs } = i18n.options;
  if (supportedLngs === false || supportedLngs === undefined) {
    return ["en-US"];
  }
  return supportedLngs;
}

async function applyLocale(
  rawLocale: string | undefined,
  signal: AbortSignal
): Promise<void> {
  const trimmed = typeof rawLocale === "string" ? rawLocale.trim() : undefined;
  const profileLocale = trimmed === "" ? undefined : trimmed;

  if (profileLocale !== undefined) {
    updateStoredProfileLocale(profileLocale);
  }

  const localeForI18n = resolveProfileLocale(profileLocale, getSupportedLngs());

  await i18n.changeLanguage(localeForI18n);
  if (signal.aborted) return;

  const resolved = i18n.resolvedLanguage ?? localeForI18n;
  syncZodLocale(resolved);
  await syncDayjsLocale(resolved);

  signal.throwIfAborted();
  i18n.emit("localeReady", resolved);
}

/**
 * Syncs the i18n language with the authenticated user's OIDC profile locale.
 * On initial load, sets the language from the profile locale claim.
 * On tab focus, re-fetches the OIDC UserInfo endpoint to pick up
 * locale changes made on the auth server without requiring a re-login.
 *
 * The latest UserInfo `locale` is written to the in-memory OIDC user store when
 * present. i18n uses {@link resolveProfileLocale} so unset, invalid, or
 * unsupported profile values fall back to English.
 */
export function useLocaleSync() {
  const { user } = useAuth();
  const locale = user?.profile.locale;
  const accessToken = user?.access_token;

  const documentVisible = useVisibilityChange();
  const previouslyHidden = useRef(false);

  useEffect(() => {
    if (!user) return;

    const controller = new AbortController();
    const profileLocale =
      typeof locale === "string" ? locale.trim() || undefined : undefined;
    applyLocale(profileLocale, controller.signal).catch(() => {});

    return () => {
      controller.abort();
    };
  }, [locale, user]);

  useEffect(() => {
    if (!documentVisible) {
      previouslyHidden.current = true;
      return;
    }

    if (!previouslyHidden.current || !accessToken) return;
    previouslyHidden.current = false;

    const controller = new AbortController();

    fetchProfileLocale(accessToken, controller.signal)
      .then(async (profileLocale) => {
        if (controller.signal.aborted || profileLocale === undefined) return;

        await applyLocale(profileLocale, controller.signal);
      })
      .catch(() => {
        // Fetch failed or was aborted; locale stays as-is until next login
      });

    return () => {
      controller.abort();
    };
  }, [documentVisible, accessToken]);
}
